import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Settings, Package, Plus, Save, Trash2, X, Database, Edit2 } from 'lucide-react';
import {
  fetchAllProposalProducts, createProposalProduct, updateProposalProduct,
  deleteProposalProduct, fetchProposalSettings, updateProposalSettings,
  seedProposalCatalog
} from '../actions/proposalCatalog';
import '../styles/ProposalPricesView.css';

// ─── Design Tokens ─────────────────────────────────────────
const TEAL    = '#00b4d8';
const DARK    = '#001d3a';
const BG      = '#f4f6f9';
const CARD    = '#ffffff';
const BORDER  = '#e2e8f0';
const TEXT_1  = '#1e293b';
const TEXT_2  = '#64748b';
const RED     = '#ef4444';
const GREEN   = '#10b981';

const cardStyle   = { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: 24, marginBottom: 20 };
const labelStyle  = { fontSize: 12, fontWeight: 600, color: TEXT_2, marginBottom: 4, display: 'block' };
const inputStyle  = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT_1, outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: '#fff', cursor: 'pointer' };
const btnPrimary  = { background: TEAL, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnDanger   = { ...btnPrimary, background: RED };
const btnOutline  = { ...btnPrimary, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT_1 };
const sectionTitle = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: TEXT_2 };

const MAINT_ZONES  = ['ZONA_I', 'ZONA_II', 'ZONA_III', 'ZONA_IV'];
const MAINT_RANGES = ['0-500', '501-1000', '1001-2000'];
const MAINT_ZONE_LABELS = {
  ZONA_I:   'ZONA I',
  ZONA_II:  'ZONA II',
  ZONA_III: 'ZONA III',
  ZONA_IV:  'ZONA IV',
};

const EMPTY_PRODUCT = { category: 'cabinet', code: '', codeIndoor: '', codeOutdoor: '', name: '', lockers: 0, env: 'both', image: '', priceInterior: 0, priceExterior: 0, rentInterior: 0, rentExterior: 0, personalizationPrice: 0, personalizationCost: 0, assetCostInterior: 0, assetCostExterior: 0, widthInterior: '', widthExterior: '', sortOrder: 0 };
const RENTAB_ADMIN_IDS = ['5b03210d8aca3af35664d0e2', '5cf612434cffd40ee39fcc2c'];

const IMAGE_OPTIONS = [
  { value: '',        label: '— Ninguna —' },
  { value: 'VLK08',   label: 'VLK08 (Kiosco interior)' },
  { value: 'VLK06',   label: 'VLK06 (Kiosco exterior)' },
  { value: 'VLM02',   label: 'VLM02 (2 lockers)' },
  { value: 'VLM04',   label: 'VLM04 (4 lockers)' },
  { value: 'VLM04XL', label: 'VLM04XL (4 lockers XL)' },
  { value: 'VLM06',   label: 'VLM06 (6 lockers)' },
  { value: 'VLM06XL', label: 'VLM06XL (6 lockers XL)' },
  { value: 'VLM10',   label: 'VLM10 (10 lockers)' },
  { value: 'VLM12',   label: 'VLM12 (12 lockers)' },
  { value: 'qr',      label: 'QR Panel' },
];

class ProposalPricesView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editingCell: null, // { id, field }
      cellValue: '',
      addingProduct: false,
      newProduct: { ...EMPTY_PRODUCT },
      editSettings: false,
      localSettings: null,
    };
  }

  componentDidMount() {
    this.props.fetchAllProposalProducts();
    this.props.fetchProposalSettings();
  }

  componentDidUpdate(prevProps) {
    if (this.props.needsRefresh && !prevProps.needsRefresh) {
      this.props.fetchAllProposalProducts();
    }
    if (this.props.settings && !this.state.localSettings) {
      this.setState({ localSettings: this.toPlainSettings(this.props.settings) });
    }
  }

  toPlainSettings(s) {
    const lp = s.licensePricing || {};
    const mp = s.maintenancePricing || {};
    const buildMatrix = (typeData, defaults) => {
      const result = {};
      MAINT_ZONES.forEach((z, zi) => {
        result[z] = {};
        MAINT_RANGES.forEach((r, ri) => {
          // Support both old flat number and new nested object
          const nested = typeData && typeof typeData === 'object' && typeData[z];
          result[z][r] = (nested && nested[r] != null) ? nested[r] : (defaults[zi][ri] ?? 0);
        });
      });
      return result;
    };
    const defaultsEstandar = [
      [1500, 2250, 3000],
      [2500, 3500, 4500],
      [3000, 4250, 5500],
      [3250, 4750, 6000],
    ];
    const defaultsPremium = [
      [3100, 4250, 5750],
      [4750, 6750, 8750],
      [6000, 8500, 11000],
      [6500, 9250, 12000],
    ];
    return {
      licenseProPerPOS:                   lp.PRO?.perPOS    ?? 135,
      licenseBasicPerPOS:                 lp.BASIC?.perPOS  ?? 99,
      licenseLockerStd:                   lp.lockerStd      ?? 50,
      personalizationProjectCost:         s.personalizationProjectCost         ?? 0,
      internalLicenseCostPerLockerYear:   s.internalLicenseCostPerLockerYear   ?? 1.25,
      internalMaintCostPerLockerYear:     s.internalMaintCostPerLockerYear     ?? 3.50,
      maintenanceEstandar: buildMatrix(mp.ESTANDAR, defaultsEstandar),
      maintenancePremium:  buildMatrix(mp.PREMIUM,  defaultsPremium),
    };
  }

  handleSeed = async () => {
    await this.props.seedProposalCatalog();
    this.props.fetchAllProposalProducts();
    this.props.fetchProposalSettings();
  };

  // ─── Product CRUD ────────────────────────────────────────
  startCellEdit = (id, field, value) => {
    this.setState({ editingCell: { id, field }, cellValue: value ?? '' });
  };

  cancelCellEdit = () => {
    this.setState({ editingCell: null, cellValue: '' });
  };

  commitCellEdit = async (productId, field) => {
    const { editingCell, cellValue } = this.state;
    if (!editingCell || editingCell.id !== productId || editingCell.field !== field) return;
    const product = (this.props.products || []).find(p => p._id === productId);
    if (!product) { this.cancelCellEdit(); return; }
    const numFields = ['lockers', 'priceInterior', 'priceExterior', 'rentInterior', 'rentExterior', 'personalizationPrice', 'personalizationCost', 'assetCostInterior', 'assetCostExterior', 'sortOrder'];
    const nullableNumFields = ['widthInterior', 'widthExterior'];
    let finalValue = cellValue;
    if (numFields.includes(field)) {
      finalValue = Number(cellValue) || 0;
    } else if (nullableNumFields.includes(field)) {
      finalValue = (cellValue === '' || cellValue === null || cellValue === undefined) ? null : Number(cellValue);
    }
    this.setState({ editingCell: null, cellValue: '' });
    await this.props.updateProposalProduct(productId, { ...product, [field]: finalValue });
  };

  startAdd = () => {
    this.setState({ addingProduct: true, newProduct: { ...EMPTY_PRODUCT }, editingProduct: null });
  };

  cancelAdd = () => {
    this.setState({ addingProduct: false });
  };

  saveAdd = async () => {
    await this.props.createProposalProduct(this.state.newProduct);
    this.setState({ addingProduct: false, newProduct: { ...EMPTY_PRODUCT } });
  };

  handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este producto? Se ocultará del configurador.')) return;
    await this.props.deleteProposalProduct(id);
  };

  // ─── Settings ────────────────────────────────────────────
  saveSettings = async () => {
    const ls = this.state.localSettings;
    // Ensure all matrix values are numbers
    const numMatrix = (m) => {
      const out = {};
      MAINT_ZONES.forEach(z => {
        out[z] = {};
        MAINT_RANGES.forEach(r => { out[z][r] = Number(m[z][r]) || 0; });
      });
      return out;
    };
    await this.props.updateProposalSettings({
      licensePricing: {
        PRO:      { perPOS: Number(ls.licenseProPerPOS)   },
        BASIC:    { perPOS: Number(ls.licenseBasicPerPOS) },
        lockerStd: Number(ls.licenseLockerStd),
      },
      maintenancePricing: {
        ESTANDAR: numMatrix(ls.maintenanceEstandar),
        PREMIUM:  numMatrix(ls.maintenancePremium),
      },
      personalizationProjectCost:       Number(ls.personalizationProjectCost)       || 0,
      internalLicenseCostPerLockerYear: Number(ls.internalLicenseCostPerLockerYear) || 1.25,
      internalMaintCostPerLockerYear:   Number(ls.internalMaintCostPerLockerYear)   || 3.50,
    });
    this.setState({ editSettings: false });
  };

  // ─── Render helpers ──────────────────────────────────────
  renderProductRow(p) {
    const { editingCell, cellValue } = this.state;
    const isAdmin = RENTAB_ADMIN_IDS.includes(this.props.authId);
    const inactive = !p.isActive;
    const isEditing = (field) => editingCell && editingCell.id === p._id && editingCell.field === field;

    const cellInput = (field, type = 'text', width = 80, extraStyle = {}) => (
      <input
        autoFocus
        type={type}
        value={cellValue}
        onChange={e => this.setState({ cellValue: e.target.value })}
        onBlur={() => this.commitCellEdit(p._id, field)}
        onKeyDown={e => {
          if (e.key === 'Enter') this.commitCellEdit(p._id, field);
          if (e.key === 'Escape') this.cancelCellEdit();
        }}
        style={{ ...inputStyle, width, textAlign: type === 'number' ? 'right' : 'left', ...extraStyle }}
      />
    );

    const cellSelect = (field, options, width = 100) => (
      <select
        autoFocus
        value={cellValue}
        onChange={e => this.setState({ cellValue: e.target.value })}
        onBlur={() => this.commitCellEdit(p._id, field)}
        style={{ ...selectStyle, width }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );

    const click = (field, raw) => ({
      onClick: () => this.startCellEdit(p._id, field, raw),
      title: 'Clic para editar',
    });

    return (
      <tr key={p._id} style={{ background: inactive ? '#fef2f2' : 'transparent', opacity: inactive ? 0.5 : 1 }}>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('category', p.category)}>
          {isEditing('category')
            ? cellSelect('category', [{ value: 'cabinet', label: 'Armario' }, { value: 'pos', label: 'Punto Venta' }])
            : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: p.category === 'cabinet' ? '#e0f2fe' : '#fef3c7', color: p.category === 'cabinet' ? '#0369a1' : '#92400e', fontWeight: 600 }}>{p.category === 'cabinet' ? 'Armario' : 'P.Venta'}</span>
          }
        </td>
        <td style={{ ...tdS, fontWeight: 600, cursor: 'pointer' }} {...click('code', p.code)}>
          {isEditing('code') ? cellInput('code', 'text', 80) : p.code}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('codeIndoor', p.codeIndoor || '')}>
          {isEditing('codeIndoor') ? cellInput('codeIndoor', 'text', 90) : <span style={{ fontSize: 11, color: '#0369a1' }}>{p.codeIndoor || '—'}</span>}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('codeOutdoor', p.codeOutdoor || '')}>
          {isEditing('codeOutdoor') ? cellInput('codeOutdoor', 'text', 90) : <span style={{ fontSize: 11, color: '#92400e' }}>{p.codeOutdoor || '—'}</span>}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('name', p.name)}>
          {isEditing('name') ? cellInput('name', 'text', 200) : p.name}
        </td>
        <td style={{ ...tdS, textAlign: 'center', cursor: 'pointer' }} {...click('lockers', p.lockers)}>
          {isEditing('lockers') ? cellInput('lockers', 'number', 50) : p.lockers}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('env', p.env || 'both')}>
          {isEditing('env')
            ? cellSelect('env', [{ value: 'indoor', label: 'Interior' }, { value: 'outdoor', label: 'Exterior' }, { value: 'both', label: 'Ambos' }], 90)
            : <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: p.env === 'indoor' ? '#dbeafe' : p.env === 'outdoor' ? '#fef3c7' : '#e2e8f0', color: p.env === 'indoor' ? '#1e40af' : p.env === 'outdoor' ? '#92400e' : '#475569', fontWeight: 600 }}>{p.env === 'indoor' ? 'Interior' : p.env === 'outdoor' ? 'Exterior' : 'Ambos'}</span>
          }
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('image', p.image || '')}>
          {isEditing('image')
            ? cellSelect('image', IMAGE_OPTIONS, 120)
            : <span style={{ fontSize: 11 }}>{p.image || '—'}</span>
          }
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('personalizationCost', p.personalizationCost || 0)}>
            {isEditing('personalizationCost') ? cellInput('personalizationCost', 'number', 70, { background: '#fffbeb' }) : `${(p.personalizationCost || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('personalizationPrice', p.personalizationPrice || 0)}>
          {isEditing('personalizationPrice') ? cellInput('personalizationPrice', 'number', 70) : `${p.personalizationPrice || 0} €`}
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('assetCostInterior', p.assetCostInterior || 0)}>
            {isEditing('assetCostInterior') ? cellInput('assetCostInterior', 'number', 80, { background: '#fffbeb' }) : `${(p.assetCostInterior || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('priceInterior', p.priceInterior)}>
          {isEditing('priceInterior') ? cellInput('priceInterior', 'number', 80) : `${p.priceInterior.toLocaleString()} €`}
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('assetCostExterior', p.assetCostExterior || 0)}>
            {isEditing('assetCostExterior') ? cellInput('assetCostExterior', 'number', 80, { background: '#fffbeb' }) : `${(p.assetCostExterior || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('priceExterior', p.priceExterior)}>
          {isEditing('priceExterior') ? cellInput('priceExterior', 'number', 80) : `${p.priceExterior.toLocaleString()} €`}
        </td>
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer', color: p.widthInterior != null ? TEXT_1 : TEXT_2 }} {...click('widthInterior', p.widthInterior ?? '')}>
          {isEditing('widthInterior') ? cellInput('widthInterior', 'number', 70) : p.widthInterior != null ? `${p.widthInterior} cm` : 'auto'}
        </td>
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer', color: p.widthExterior != null ? TEXT_1 : TEXT_2 }} {...click('widthExterior', p.widthExterior ?? '')}>
          {isEditing('widthExterior') ? cellInput('widthExterior', 'number', 70) : p.widthExterior != null ? `${p.widthExterior} cm` : 'auto'}
        </td>
        <td style={{ ...tdS, textAlign: 'center', cursor: 'pointer' }} {...click('sortOrder', p.sortOrder)}>
          {isEditing('sortOrder') ? cellInput('sortOrder', 'number', 50) : p.sortOrder}
        </td>
        <td style={tdS}>
          {p.isActive && (
            <button onClick={() => this.handleDelete(p._id)} style={{ ...btnDanger, padding: '5px 8px', fontSize: 11 }}><Trash2 size={12} /></button>
          )}
        </td>
      </tr>
    );
  }

  renderNewRow() {
    const np = this.state.newProduct;
    const set = (field) => (e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, [field]: e.target.value } }));
    const setN = (field) => (e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, [field]: Number(e.target.value) || 0 } }));
    return (
      <tr style={{ background: '#f0fdf4' }}>
        <td style={tdS}><select value={np.category} onChange={set('category')} style={{ ...selectStyle, width: 100 }}><option value="cabinet">Armario</option><option value="pos">Punto Venta</option></select></td>
        <td style={tdS}><input value={np.code} onChange={set('code')} placeholder="LK12" style={{ ...inputStyle, width: 80 }} /></td>
        <td style={tdS}><input value={np.codeIndoor || ''} onChange={set('codeIndoor')} placeholder="LK12-INT" style={{ ...inputStyle, width: 90 }} /></td>
        <td style={tdS}><input value={np.codeOutdoor || ''} onChange={set('codeOutdoor')} placeholder="LK12-EXT" style={{ ...inputStyle, width: 90 }} /></td>
        <td style={tdS}><input value={np.name} onChange={set('name')} placeholder="Nombre del producto" style={{ ...inputStyle, width: 200 }} /></td>
        <td style={tdS}><input type="number" value={np.lockers} onChange={setN('lockers')} style={{ ...inputStyle, width: 50, textAlign: 'center' }} /></td>
        <td style={tdS}><select value={np.env || 'both'} onChange={set('env')} style={{ ...selectStyle, width: 90 }}><option value="indoor">Interior</option><option value="outdoor">Exterior</option><option value="both">Ambos</option></select></td>
        <td style={tdS}><select value={np.image || ''} onChange={set('image')} style={{ ...selectStyle, width: 110 }}>{IMAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></td>
        {RENTAB_ADMIN_IDS.includes(this.props.authId) && <td style={{ ...tdS, background: '#fffbeb' }}><input type="number" value={np.personalizationCost || 0} onChange={setN('personalizationCost')} style={{ ...inputStyle, width: 70, textAlign: 'right', background: '#fffbeb' }} /></td>}
        <td style={tdS}><input type="number" value={np.personalizationPrice || 0} onChange={setN('personalizationPrice')} style={{ ...inputStyle, width: 70, textAlign: 'right' }} /></td>
        {RENTAB_ADMIN_IDS.includes(this.props.authId) && <td style={{ ...tdS, background: '#fffbeb' }}><input type="number" value={np.assetCostInterior || 0} onChange={setN('assetCostInterior')} style={{ ...inputStyle, width: 80, textAlign: 'right', background: '#fffbeb' }} /></td>}
        <td style={tdS}><input type="number" value={np.priceInterior} onChange={setN('priceInterior')} style={{ ...inputStyle, width: 80, textAlign: 'right' }} /></td>
        {RENTAB_ADMIN_IDS.includes(this.props.authId) && <td style={{ ...tdS, background: '#fffbeb' }}><input type="number" value={np.assetCostExterior || 0} onChange={setN('assetCostExterior')} style={{ ...inputStyle, width: 80, textAlign: 'right', background: '#fffbeb' }} /></td>}
        <td style={tdS}><input type="number" value={np.priceExterior} onChange={setN('priceExterior')} style={{ ...inputStyle, width: 80, textAlign: 'right' }} /></td>
        <td style={tdS}><input type="number" step="0.1" value={np.widthInterior ?? ''} onChange={(e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, widthInterior: e.target.value === '' ? null : Number(e.target.value) } }))} placeholder="auto" style={{ ...inputStyle, width: 70, textAlign: 'right' }} /></td>
        <td style={tdS}><input type="number" step="0.1" value={np.widthExterior ?? ''} onChange={(e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, widthExterior: e.target.value === '' ? null : Number(e.target.value) } }))} placeholder="auto" style={{ ...inputStyle, width: 70, textAlign: 'right' }} /></td>
        <td style={tdS}><input type="number" value={np.sortOrder} onChange={setN('sortOrder')} style={{ ...inputStyle, width: 50, textAlign: 'center' }} /></td>
        <td style={tdS}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={this.saveAdd} style={{ ...btnPrimary, padding: '6px 10px', fontSize: 12 }}><Save size={12} /> Añadir</button>
            <button onClick={this.cancelAdd} style={{ ...btnOutline, padding: '6px 10px', fontSize: 12 }}><X size={12} /></button>
          </div>
        </td>
      </tr>
    );
  }

  render() {
    const { products, loading } = this.props;
    const { addingProduct, editSettings, localSettings } = this.state;
    const hasProducts = products && products.length > 0;

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: BG, minHeight: '100vh', padding: '24px 24px 60px' }}>
        {/* Header */}
        <div className="pprice-header">
          <div className="pprice-header-content">
            <div className="pprice-header-text">
              <h1>
                <Settings size={28} className="pprice-header-icon" />
                Catálogo de Precios
              </h1>
              <span className="pprice-subtitle">Gestiona productos y precios del configurador de propuestas</span>
            </div>
            <div className="pprice-header-actions">
              {!hasProducts && (
                <button onClick={this.handleSeed} disabled={loading} className="pprice-header-btn primary">
                  <Database size={14} /> Inicializar catálogo
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          {/* Settings Card */}
          {localSettings && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={sectionTitle}>Tarifas Generales</div>
                {!editSettings ? (
                  <button onClick={() => this.setState({ editSettings: true })} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12 }}><Edit2 size={12} /> Editar</button>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={this.saveSettings} style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}><Save size={12} /> Guardar</button>
                    <button onClick={() => this.setState({ editSettings: false, localSettings: this.toPlainSettings(this.props.settings) })} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12 }}><X size={12} /></button>
                  </div>
                )}
              </div>
              {/* License prices + Maintenance matrices in columns */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'start' }}>
                {/* License prices */}
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_2, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Licencias</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[['BÁSICA (€/PdV/mes)', 'licenseBasicPerPOS'], ['PRO (€/PdV/mes)', 'licenseProPerPOS'], ['Baterías (€/ud)', 'licenseLockerStd'], ['Personalización (€/proyecto)', 'personalizationProjectCost'], ['Coste lic. interno (€/taq/año)', 'internalLicenseCostPerLockerYear'], ['Coste mant. interno (€/taq/año)', 'internalMaintCostPerLockerYear']].map(([lbl, key]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ ...labelStyle, marginBottom: 0, minWidth: 130, whiteSpace: 'nowrap' }}>{lbl}</label>
                        {editSettings ? (
                          <input type="number" step="0.01" value={localSettings[key]}
                            onChange={(e) => this.setState(prev => ({ localSettings: { ...prev.localSettings, [key]: e.target.value } }))}
                            style={{ ...inputStyle, width: 90, textAlign: 'right' }} />
                        ) : (
                          <span style={{ fontSize: 15, fontWeight: 700, color: TEXT_1 }}>{Number(localSettings[key]).toFixed(2)} €</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Maintenance matrices */}
                {[['ESTANDAR', 'maintenanceEstandar', 'Mantenimiento ESTÁNDAR'], ['PREMIUM', 'maintenancePremium', 'Mantenimiento PREMIUM']].map(([, matKey, title]) => (
                  <div key={matKey} style={{ flex: '1 1 400px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_2, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title} (€/año)</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={thCellS}>Taquillas</th>
                            {MAINT_ZONES.map(z => <th key={z} style={thCellS}>{MAINT_ZONE_LABELS[z]}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {MAINT_RANGES.map(range => (
                            <tr key={range}>
                              <td style={{ ...tdCellS, fontWeight: 600, color: TEXT_2, textAlign: 'left' }}>{range}</td>
                              {MAINT_ZONES.map(zone => (
                                <td key={zone} style={tdCellS}>
                                  {editSettings ? (
                                    <input type="number" value={localSettings[matKey][zone][range]}
                                      onChange={(e) => this.setState(prev => ({
                                        localSettings: {
                                          ...prev.localSettings,
                                          [matKey]: {
                                            ...prev.localSettings[matKey],
                                            [zone]: { ...prev.localSettings[matKey][zone], [range]: e.target.value }
                                          }
                                        }
                                      }))}
                                      style={{ ...inputStyle, width: 90, textAlign: 'right', padding: '4px 8px' }} />
                                  ) : (
                                    <span style={{ fontWeight: 600 }}>{Number(localSettings[matKey][zone][range]).toLocaleString('es-ES')} €</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products Table */}
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={16} color={TEAL} />
                <span style={sectionTitle}>Productos</span>
              </div>
              {!addingProduct && <button onClick={this.startAdd} style={{ ...btnPrimary, padding: '7px 14px', fontSize: 12 }}><Plus size={14} /> Nuevo producto</button>}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                <thead>
                  <tr>
                    {['Tipo', 'Código', 'Cód. Int.', 'Cód. Ext.', 'Nombre', 'Lockers', 'Entorno', 'Imagen'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Pers. €</th>}
                    <th style={thS}>Pers. €</th>
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Int. €</th>}
                    <th style={thS}>Precio Int.</th>
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Ext. €</th>}
                    <th style={thS}>Precio Ext.</th>
                    {['Ancho Int. (cm)', 'Ancho Ext. (cm)', 'Orden', 'Acciones'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {addingProduct && this.renderNewRow()}
                  {(products || []).map(p => this.renderProductRow(p))}
                  {!loading && hasProducts === false && (
                    <tr><td colSpan={12} style={{ padding: 40, textAlign: 'center', color: TEXT_2 }}>
                      No hay productos. Usa "Inicializar catálogo" para cargar valores por defecto.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const thS = { padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0e7490', background: '#f0fdfa', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' };
const tdS = { padding: '8px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', verticalAlign: 'middle' };
const thCellS = { padding: '7px 12px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0e7490', background: '#f0fdfa', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', textAlign: 'center' };
const tdCellS = { padding: '6px 12px', border: '1px solid #e2e8f0', fontSize: 13, color: '#1e293b', textAlign: 'right' };

const mapStateToProps = (state) => ({
  products: state.proposalCatalog.products,
  settings: state.proposalCatalog.settings,
  needsRefresh: state.proposalCatalog.needsRefresh,
  loading: state.proposalCatalog.loading,
  error: state.proposalCatalog.error,
  role: state.auth.role,
  type: state.auth.type,
  authId: state.auth.id,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchAllProposalProducts, createProposalProduct, updateProposalProduct,
  deleteProposalProduct, fetchProposalSettings, updateProposalSettings,
  seedProposalCatalog,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProposalPricesView);
